import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Input, Row, Select, Typography, DatePicker, Form, message, Checkbox, Upload } from "antd";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import dayjs from 'dayjs';
import axiosInstance from '../api/axios';
import { getCustomColumns, CustomColumn } from '../api/customColumns';

const { Option } = Select;
const { Text } = Typography;

const ScreenContainer = styled.div`
  width: 400px;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const ScreenTitle = styled(Typography.Title)`
  margin: 16px !important;
  font-size: 16px !important;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const StyledInput = styled(Input)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    padding: 4px 11px !important;
    background-color: #fafafa !important;
    border-radius: 4px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;
  width: 100%;
`;

const StyledText = styled(Text)`
  color: #8c8c8c;
  font-weight: 500;
  font-size: 12px;
`;

const ButtonsRow = styled(Row)`
  padding: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
  
  .ant-btn {
    height: 40px;
    width: 178px;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label {
    padding: 0 !important;
    
    > label {
      font-size: 12px;
      color: #8c8c8c;
      font-weight: 500;
      height: auto;
      margin-bottom: 0px !important;
      line-height: 0 !important;
      
      &::before {
        display: none !important;
      }
      
      &::after {
        display: none;
      }
    }
  }
  
  .ant-form-item-control {
    margin-top: 0px;
  }
  
  .ant-form-item-explain {
    min-height: 0;
    margin-top: 0px;
    font-size: 12px;
  }
`;

interface AddInstrumentsProps {
  onCancel: () => void;
  type: string;
  laboratoryId: string;
  onSuccess: () => void;
}

const AddInstruments: React.FC<AddInstrumentsProps> = ({ onCancel, type, laboratoryId, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
    const [ownershipDocumentFile, setOwnershipDocumentFile] = useState<File | null>(null);

    // Загружаем кастомные колонки при монтировании компонента
    useEffect(() => {
        const fetchCustomColumns = async () => {
            if (!laboratoryId) return;
            
            let registryType = '';
            switch (type) {
                case 'Средства измерения (СИ)':
                    registryType = 'measuring_instrument';
                    break;
                case 'Испытательное (ИО)':
                    registryType = 'testing_instrument';
                    break;
                case 'Вспомогательное (ВО)':
                    registryType = 'helping_instrument';
                    break;
            }
            
            try {
                const columns = await getCustomColumns(registryType, Number(laboratoryId));
                setCustomColumns(columns);
            } catch (error) {
                console.error('Ошибка при загрузке кастомных колонок:', error);
            }
        };

        fetchCustomColumns();
    }, [laboratoryId, type]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Отладочная информация
            console.log('Значения формы:', values);

            // Собираем кастомные поля
            const customFieldValues: Record<string, any> = {};
            customColumns.forEach(column => {
                if (values[`custom_${column.key}`] !== undefined) {
                    customFieldValues[column.key] = values[`custom_${column.key}`];
                    // Удаляем из основных значений
                    delete values[`custom_${column.key}`];
                }
            });

            let endpoint;
            switch (type) {
                case 'Средства измерения (СИ)': {
                    endpoint = `/instruments/laboratory/${laboratoryId}/measuring`;

                    // Для средств измерения формируем правильную структуру данных
                    const measuringData = {
                        number: values.number || 1,
                        status: values.status,
                        characteristics: values.characteristics || '',
                        name: values.name,
                        manufacturer: values.manufacturer || '',
                        commissioning_year: values.commissioning_year ? dayjs(values.commissioning_year).year() : null,
                        measurement_range: values.measurement_range || '',
                        accuracy: values.accuracy_class,
                        verification_certificate: values.certificate_number || '',
                        verification_date: values.verification_date ? dayjs(values.verification_date).format('YYYY-MM-DD') : null,
                        next_verification_date: values.next_verification_date ? dayjs(values.next_verification_date).format('YYYY-MM-DD') : null,
                        registration_number: values.registration_number || '',
                        verification_method: values.verification_method,
                        ownership_document: ownershipDocumentFile ? ownershipDocumentFile.name : null,
                        location: values.location || '',
                        notes: values.notes || '',
                        custom_columns: customFieldValues
                    };

                    console.log('Подготовленные данные:', measuringData);

                    // Создаем новый FormData
                    const formData = new FormData();
                    formData.append('instrument_data', JSON.stringify(measuringData));
                    
                    // Добавляем файл, если он есть
                    if (ownershipDocumentFile) {
                        formData.append('ownership_document', ownershipDocumentFile);
                    }

                    try {
                        const response = await axiosInstance.post(endpoint, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        console.log('Успешный ответ:', response.data);
                        message.success('Запись успешно добавлена');
                        form.resetFields();
                        setOwnershipDocumentFile(null);
                        onSuccess();
                        onCancel();
                    } catch (error: any) {
                        console.error('Ошибка при отправке данных:', error);
                        console.error('Детали ошибки:', error.response?.data);
                        if (error.response?.data?.detail) {
                            if (Array.isArray(error.response.data.detail)) {
                                error.response.data.detail.forEach((err: any) => {
                                    message.error(`Ошибка: ${err.loc.join('.')} - ${err.msg}`);
                                });
                            } else {
                                message.error(`Ошибка: ${error.response.data.detail}`);
                            }
                        } else {
                            message.error('Ошибка при добавлении записи');
                        }
                    }
                    break;
                }
                case 'Испытательное (ИО)': {
                    endpoint = `/instruments/laboratory/${laboratoryId}/testing`;

                    console.log('=== Отладочная информация ===');
                    console.log('Исходные значения формы:', values);

                    // Для испытательного оборудования формируем правильную структуру данных
                    const testingData = {
                        number: values.number || 1,
                        status: values.status,
                        characteristics: values.characteristics,
                        test_groups: values.test_groups,
                        testing_equipment: values.testing_equipment,
                        manufacturer: values.manufacturer,
                        technical_characteristics: values.technical_characteristics,
                        commissioning_year: values.commissioning_year ? dayjs(values.commissioning_year).year() : null,
                        document_date_and_number: values.document_date_and_number,
                        location: values.location,
                        notes: values.notes,
                        ownership_document: ownershipDocumentFile ? ownershipDocumentFile.name : null,
                        custom_columns: customFieldValues
                    };

                    // Подробная отладочная информация
                    console.log('Подготовленные данные:', testingData);
                    console.log('Файл:', ownershipDocumentFile);
                    
                    // Создаем новый FormData
                    const formData = new FormData();
                    formData.append('instrument_data', JSON.stringify(testingData));
                    
                    // Добавляем файл, если он есть
                    if (ownershipDocumentFile) {
                        formData.append('ownership_document', ownershipDocumentFile);
                    }

                    console.log('Данные для отправки:', JSON.stringify(testingData, null, 2));
                    console.log('=== Конец отладочной информации ===');

                    try {
                        const response = await axiosInstance.post(endpoint, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        console.log('Успешный ответ:', response.data);
                        message.success('Запись успешно добавлена');
                        form.resetFields();
                        setOwnershipDocumentFile(null);
                        onSuccess();
                        onCancel();
                    } catch (error: any) {
                        console.error('Ошибка при отправке данных:', error);
                        console.error('Детали ошибки:', error.response?.data);
                        if (error.response?.data?.detail) {
                            if (Array.isArray(error.response.data.detail)) {
                                error.response.data.detail.forEach((err: any) => {
                                    message.error(`Ошибка: ${err.loc.join('.')} - ${err.msg}`);
                                });
                            } else {
                                message.error(`Ошибка: ${error.response.data.detail}`);
                            }
                        } else {
                            message.error('Ошибка при добавлении записи');
                        }
                    }
                    break;
                }
                case 'Вспомогательное (ВО)':
                    endpoint = `/instruments/laboratory/${laboratoryId}/helping`;

                    // Для вспомогательного оборудования формируем правильную структуру данных
                    const helpingData = {
                        number: values.number || 1,
                        status: values.status,
                        name: values.name,
                        manufacturer: values.manufacturer,
                        commissioning_year: values.commissioning_year ? dayjs(values.commissioning_year).year() : null,
                        purpose: values.purpose,
                        ownership_document: ownershipDocumentFile ? ownershipDocumentFile.name : null,
                        location: values.location || '',
                        notes: values.notes || '',
                        custom_columns: customFieldValues
                    };

                    // Создаем новый FormData
                    const formData = new FormData();
                    formData.append('instrument_data', JSON.stringify(helpingData));
                    
                    // Добавляем файл, если он есть
                    if (ownershipDocumentFile) {
                        formData.append('ownership_document', ownershipDocumentFile);
                    }

                    try {
                        const response = await axiosInstance.post(endpoint, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        console.log('Успешный ответ:', response.data);
                        message.success('Запись успешно добавлена');
                        form.resetFields();
                        setOwnershipDocumentFile(null);
                        onSuccess();
                        onCancel();
                    } catch (error: any) {
                        console.error('Ошибка при отправке данных:', error);
                        console.error('Детали ошибки:', error.response?.data);
                        if (error.response?.data?.detail) {
                            if (Array.isArray(error.response.data.detail)) {
                                error.response.data.detail.forEach((err: any) => {
                                    message.error(`Ошибка: ${err.loc.join('.')} - ${err.msg}`);
                                });
                            } else {
                                message.error(`Ошибка: ${error.response.data.detail}`);
                            }
                        } else {
                            message.error('Ошибка при добавлении записи');
                        }
                    }
                    break;
                default:
                    throw new Error('Invalid instrument type');
            }
        } catch (error) {
            console.error('Ошибка валидации формы:', error);
            message.error('Пожалуйста, проверьте правильность заполнения полей');
        } finally {
            setLoading(false);
        }
    };

    // Функция для рендеринга кастомных полей
    const renderCustomFields = () => {
        return customColumns.map(column => (
            <Col span={24} key={column.key}>
                <StyledFormItem
                    name={`custom_${column.key}`}
                    label={column.title}
                    rules={[{ required: column.required, message: `Введите ${column.title.toLowerCase()}` }]}
                >
                    {renderCustomFieldInput(column)}
                </StyledFormItem>
            </Col>
        ));
    };

    // Функция для рендеринга конкретного типа кастомного поля
    const renderCustomFieldInput = (column: CustomColumn) => {
        switch (column.type) {
            case 'text':
                return <StyledInput placeholder={`введите ${column.title.toLowerCase()}`} />;
            case 'int':
                return <StyledInput type="number" placeholder={`введите ${column.title.toLowerCase()}`} />;
            case 'date':
                return <StyledDatePicker format="DD.MM.YYYY" />;
            case 'boolean':
                return <Checkbox>{column.title}</Checkbox>;
            default:
                return <StyledInput placeholder={`введите ${column.title.toLowerCase()}`} />;
        }
    };

    const renderFields = () => {
        switch (type) {
            case 'Средства измерения (СИ)':
                return (
                    <>
                        <Col span={24}>
                            <StyledFormItem
                                name="status"
                                label="Статус оборудования"
                                rules={[{ required: true, message: 'Выберите статус оборудования' }]}
                            >
                                <StyledSelect placeholder="выберите статус">
                                    <Option value="В работе">В работе</Option>
                                    <Option value="На поверке">На поверке</Option>
                                    <Option value="В ремонте">В ремонте</Option>
                                    <Option value="Списан">Списан</Option>
                                </StyledSelect>
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="characteristics"
                                label="Наименование определяемых характеристик"
                            >
                                <StyledInput placeholder="введите характеристики" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="name"
                                label="Наименование СИ"
                                rules={[{ required: true, message: 'Введите наименование СИ' }]}
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="manufacturer"
                                label="Изготовитель"
                            >
                                <StyledInput placeholder="введите изготовителя" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="commissioning_year"
                                label="Год ввода в эксплуатацию"
                                rules={[{ required: true, message: 'Введите год ввода в эксплуатацию' }]}
                            >
                                <StyledDatePicker picker="year" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="measurement_range"
                                label="Диапазон измерений"
                            >
                                <StyledInput placeholder="введите диапазон" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="accuracy_class"
                                label="Класс точности, погрешность"
                                rules={[{ required: true, message: 'Введите класс точности' }]}
                            >
                                <StyledInput placeholder="введите класс точности" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="certificate_number"
                                label="Свидетельство о проверке"
                            >
                                <StyledInput placeholder="введите номер свидетельства" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="verification_date"
                                label="Дата проверки / калибровки"
                                rules={[{ required: true, message: 'Введите дату проверки' }]}
                            >
                                <StyledDatePicker format="DD.MM.YYYY" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="next_verification_date"
                                label="Дата проведения следующей проверки"
                                rules={[{ required: true, message: 'Введите дату следующей проверки' }]}
                            >
                                <StyledDatePicker format="DD.MM.YYYY" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="registration_number"
                                label="Регистрационный номер"
                            >
                                <StyledInput placeholder="введите номер" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="verification_method"
                                label="Методика проверки"
                                rules={[{ required: true, message: 'Введите методику проверки' }]}
                            >
                                <StyledInput placeholder="введите методику" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="ownership_document"
                                label="Право собственности или документ"
                            >
                                <Row gutter={8} align="middle">
                                    <Col flex="auto">
                                        <StyledInput
                                            value={form.getFieldValue('ownership_document') || 'Файл не выбран'}
                                            readOnly
                                            placeholder="Файл не выбран"
                                        />
                                    </Col>
                                    <Col>
                                        <Upload
                                            accept=".pdf,.doc,.docx,.txt"
                                            showUploadList={false}
                                            beforeUpload={(file) => {
                                                const maxSize = 20 * 1024 * 1024; // 20MB
                                                if (file.size > maxSize) {
                                                    message.error('Размер файла не должен превышать 20MB');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                setOwnershipDocumentFile(file);
                                                form.setFieldsValue({ ownership_document: file.name });
                                                return false;
                                            }}
                                        >
                                            <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                                        </Upload>
                                    </Col>
                                    {form.getFieldValue('ownership_document') && (
                                        <Col>
                                            <Button
                                                style={{ height: 40 }}
                                                icon={<DeleteOutlined />}
                                                onClick={() => {
                                                    setOwnershipDocumentFile(null);
                                                    form.setFieldsValue({ ownership_document: undefined });
                                                }}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                    До 20МБ. Форматы: PDF, DOC, DOCX, TXT
                                </div>
                            </StyledFormItem>
                        </Col>
                        
                        {/* Добавляем кастомные поля */}
                        {renderCustomFields()}
                    </>
                );

            case 'Испытательное (ИО)':
                return (
                    <>
                        <Col span={24}>
                            <StyledFormItem
                                name="status"
                                label="Статус оборудования"
                                rules={[{ required: true, message: 'Выберите статус' }]}
                            >
                                <StyledSelect placeholder="выберите статус">
                                    <Option value="В работе">В работе</Option>
                                    <Option value="На аттестации">На аттестации</Option>
                                    <Option value="В ремонте">В ремонте</Option>
                                    <Option value="Списан">Списан</Option>
                                </StyledSelect>
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="characteristics"
                                label="Наименование определяемых характеристик"
                            >
                                <StyledInput placeholder="введите характеристики" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="test_groups"
                                label="Наименование испытуемых групп объектов"
                                rules={[{ required: true, message: 'Введите название' }]}
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="testing_equipment"
                                label="Наименование испытательного оборудования"
                                rules={[{ required: true, message: 'Введите название' }]}
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="manufacturer"
                                label="Изготовитель"
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="technical_characteristics"
                                label="Основные тех. характеристики"
                            >
                                <StyledInput placeholder="введите характеристики" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="commissioning_year"
                                label="Год ввода в эксплуатацию"
                                rules={[{ required: true, message: 'Введите год' }]}
                            >
                                <StyledDatePicker picker="year" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="document_date_and_number"
                                label="Дата и номер документа"
                            >
                                <StyledInput placeholder="введите данные" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="ownership_document"
                                label="Право собственности или документ"
                            >
                                <Row gutter={8} align="middle">
                                    <Col flex="auto">
                                        <StyledInput
                                            value={form.getFieldValue('ownership_document') || 'Файл не выбран'}
                                            readOnly
                                            placeholder="Файл не выбран"
                                        />
                                    </Col>
                                    <Col>
                                        <Upload
                                            accept=".pdf,.doc,.docx,.txt"
                                            showUploadList={false}
                                            beforeUpload={(file) => {
                                                const maxSize = 20 * 1024 * 1024; // 20MB
                                                if (file.size > maxSize) {
                                                    message.error('Размер файла не должен превышать 20MB');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                setOwnershipDocumentFile(file);
                                                form.setFieldsValue({ ownership_document: file.name });
                                                return false;
                                            }}
                                        >
                                            <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                                        </Upload>
                                    </Col>
                                    {form.getFieldValue('ownership_document') && (
                                        <Col>
                                            <Button
                                                style={{ height: 40 }}
                                                icon={<DeleteOutlined />}
                                                onClick={() => {
                                                    setOwnershipDocumentFile(null);
                                                    form.setFieldsValue({ ownership_document: undefined });
                                                }}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                    До 20МБ. Форматы: PDF, DOC, DOCX, TXT
                                </div>
                            </StyledFormItem>
                        </Col>
                        
                        {/* Добавляем кастомные поля */}
                        {renderCustomFields()}
                    </>
                );

            case 'Вспомогательное (ВО)':
                return (
                    <>
                        <Col span={24}>
                            <StyledFormItem
                                name="status"
                                label="Статус оборудования"
                                rules={[{ required: true, message: 'Выберите статус' }]}
                            >
                                <StyledSelect placeholder="выберите статус">
                                    <Option value="В работе">В работе</Option>
                                    <Option value="На обслуживании">На обслуживании</Option>
                                    <Option value="В ремонте">В ремонте</Option>
                                    <Option value="Списан">Списан</Option>
                                </StyledSelect>
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="name"
                                label="Наименование"
                                rules={[{ required: true, message: 'Введите название' }]}
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="manufacturer"
                                label="Изготовитель"
                                rules={[{ required: true, message: 'Введите название' }]}
                            >
                                <StyledInput placeholder="введите название" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="commissioning_year"
                                label="Год ввода в эксплуатацию"
                                rules={[{ required: true, message: 'Введите год ввода в эксплуатацию' }]}
                            >
                                <StyledDatePicker picker="year" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="purpose"
                                label="Назначение"
                                rules={[{ required: true, message: 'Введите назначение' }]}
                            >
                                <StyledInput placeholder="введите назначение" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="ownership_document"
                                label="Право собственности или документ"
                            >
                                <Row gutter={8} align="middle">
                                    <Col flex="auto">
                                        <StyledInput
                                            value={form.getFieldValue('ownership_document') || 'Файл не выбран'}
                                            readOnly
                                            placeholder="Файл не выбран"
                                        />
                                    </Col>
                                    <Col>
                                        <Upload
                                            accept=".pdf,.doc,.docx,.txt"
                                            showUploadList={false}
                                            beforeUpload={(file) => {
                                                const maxSize = 20 * 1024 * 1024; // 20MB
                                                if (file.size > maxSize) {
                                                    message.error('Размер файла не должен превышать 20MB');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                setOwnershipDocumentFile(file);
                                                form.setFieldsValue({ ownership_document: file.name });
                                                return false;
                                            }}
                                        >
                                            <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                                        </Upload>
                                    </Col>
                                    {form.getFieldValue('ownership_document') && (
                                        <Col>
                                            <Button
                                                style={{ height: 40 }}
                                                icon={<DeleteOutlined />}
                                                onClick={() => {
                                                    setOwnershipDocumentFile(null);
                                                    form.setFieldsValue({ ownership_document: undefined });
                                                }}
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                    До 20МБ. Форматы: PDF, DOC, DOCX, TXT
                                </div>
                            </StyledFormItem>
                        </Col>
                        
                        {/* Добавляем кастомные поля */}
                        {renderCustomFields()}
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <ScreenContainer>
            <ScreenTitle level={4}>Новая запись</ScreenTitle>
            
            <ScrollContent>
                <StyledForm form={form} layout="vertical">
                    <Row gutter={[16, 16]}>
                        {renderFields()}
                        
                        {/* Общие поля для всех типов */}
                        <Col span={24}>
                            <StyledFormItem
                                name="location"
                                label="Место установки или хранения"
                            >
                                <StyledInput placeholder="введите место" />
                            </StyledFormItem>
                        </Col>
                        <Col span={24}>
                            <StyledFormItem
                                name="notes"
                                label="Примечание"
                            >
                                <StyledInput placeholder="введите примечание" />
                            </StyledFormItem>
                        </Col>
                    </Row>
                </StyledForm>
            </ScrollContent>

            <ButtonsRow>
                <Button onClick={onCancel} disabled={loading}>Отмена</Button>
                <Button type="primary" onClick={handleSubmit} loading={loading}>
                    Сохранить
                </Button>
            </ButtonsRow>
        </ScreenContainer>
    );
};

export default AddInstruments;

